import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';
import { useLanguage } from '../../i18n/LanguageContext';
const WHATSAPP_URL = 'https://wa.me/77010346443?text=%D1%85%D0%BE%D1%87%D1%83%20%D0%B4%D0%B5%D0%BC%D0%BE%20Meelo';
type Tab = 'web' | 'mobile';
function PillSwitch({
  active,
  onChange
}: {
  active: Tab;
  onChange: (t: Tab) => void;
}) {
  const {
    t
  } = useLanguage();
  return <div className="inline-flex items-center rounded-full bg-white/80 backdrop-blur-md border border-gray-200 p-1 shadow-md" data-id="element-90">
      <button onClick={() => onChange('mobile')} className={`relative px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${active === 'mobile' ? 'bg-[#7C4DFF] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-91">
        {t('dashboard.switch_mobile')}
      </button>
      <button onClick={() => onChange('web')} className={`relative px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${active === 'web' ? 'bg-[#7C4DFF] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-92">
        {t('dashboard.switch_web')}
      </button>
    </div>;
}
function CtaPair() {
  const {
    t
  } = useLanguage();
  return <div className="mt-8 flex flex-col sm:flex-row items-start gap-3" data-id="element-93">
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-3 rounded-full bg-[#7C4DFF] text-white font-medium text-base shadow-lg hover:bg-[#651FFF] transition-all" data-id="element-94">
        {t('dashboard.cta_more')}
      </a>
      <span className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200" data-id="element-95">
        {t('dashboard.price')}
      </span>
    </div>;
}
export function DashboardSection() {
  const {
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState<Tab>('web');
  const previewRef = useRevealOnScroll<HTMLDivElement>(0, 0.15);
  const benefits = [{
    text: t('dashboard.benefit_1')
  }, {
    text: t('dashboard.benefit_2')
  }, {
    text: t('dashboard.benefit_3')
  }, {
    text: t('dashboard.benefit_4')
  }, {
    text: t('dashboard.benefit_5')
  }];
  return <section className="relative pt-24 pb-6 overflow-hidden" id="solutions" style={{
    background: 'linear-gradient(180deg, #7D4EFF 0%, #6E3EF0 100%)'
  }} data-id="element-96">
      {/* Subtle background blobs for depth */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true" data-id="element-97">
        <div style={{
        position: 'absolute',
        top: '10%',
        left: '-10%',
        width: 500,
        height: 500,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
        filter: 'blur(80px)'
      }} data-id="element-98" />
        <div style={{
        position: 'absolute',
        bottom: '5%',
        right: '-5%',
        width: 400,
        height: 400,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)',
        filter: 'blur(60px)'
      }} data-id="element-99" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-100">
        {/* Section intro */}
        <div className="text-center mb-16" data-id="element-101">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-white/70 mb-3" data-id="element-102">
            {t('dashboard.label')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" data-id="element-103">
            {t('dashboard.title')}
          </h2>
        </div>

        {/* Module card — white */}
        <div className="rounded-3xl border border-gray-100 bg-white shadow-xl overflow-hidden" data-id="element-104">
          <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3" data-id="element-105">
            <span className="text-xs font-bold text-gray-300 tracking-widest font-mono" data-id="element-106">
              01 / 05
            </span>
            <span className="text-xs font-semibold tracking-widest uppercase text-[#7C4DFF]" data-id="element-107">
              {t('dashboard.card_label')}
            </span>
          </div>
          <div className="p-6 lg:p-10" data-id="element-108">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center lg:min-h-[520px]" data-id="element-109">
              {/* Preview — LEFT on desktop */}
              <div className="relative lg:order-1" data-id="element-110">
                <div ref={previewRef} className="reveal-visual visual-hover-lift relative min-h-[360px] sm:min-h-[420px] flex items-center justify-center" data-id="element-111">
                  <div className={`w-full transition-all duration-300 ${activeTab === 'web' ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'}`} data-id="element-112">
                    <div className="rounded-2xl shadow-2xl border border-gray-200 overflow-hidden bg-white" data-id="element-113">
                      <div className="h-8 bg-gray-50 border-b border-gray-100 flex items-center px-4 space-x-2" data-id="element-114">
                        <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-115" />
                        <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-116" />
                        <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-117" />
                      </div>
                      <img src="https://cdn.magicpatterns.com/uploads/7Tan4tCoZC26N1PHqU6F5U/панель_для_руководителя.png" alt="Дашборд руководителя — веб" className="w-full h-auto" data-id="element-118" />
                    </div>
                  </div>
                  <div className={`w-full flex justify-center transition-all duration-300 ${activeTab === 'mobile' ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'}`} data-id="element-119">
                    <img src="https://cdn.magicpatterns.com/uploads/smrKEtBgxHCGSuzY8xQDnA/Mockuuups_Free_iPhone_Hand_Mockup.png" alt="Мобильное приложение Meelo" className="max-h-[420px] w-auto object-contain drop-shadow-2xl" data-id="element-120" />
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center z-10" data-id="element-121">
                    <PillSwitch active={activeTab} onChange={setActiveTab} data-id="element-122" />
                  </div>
                </div>
              </div>

              {/* Text — RIGHT on desktop */}
              <div className="lg:order-2" data-id="element-123">
                <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-[#1A1A2E] leading-tight mb-6" data-id="element-124">
                  {t('dashboard.card_title')}
                </h2>
                <ul className="space-y-4 mb-4" data-id="element-125">
                  {benefits.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-126">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#EDE7F6] flex items-center justify-center mt-0.5" data-id="element-127">
                        <Check className="w-3.5 h-3.5 text-[#7C4DFF]" strokeWidth={3} data-id="element-128" />
                      </div>
                      <span className="text-gray-700 text-base leading-relaxed" data-id="element-129">
                        {item.text}
                      </span>
                    </li>)}
                </ul>
                <CtaPair data-id="element-130" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}